import type { InputHTMLAttributes } from "react"

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  error?: string
}

/**
 * Every field: visible label (never placeholder-as-label), and an error
 * message shown below the field when present.
 *
 * The error slot is positioned absolutely, anchored below the wrapper's
 * own bottom edge (`top: "100%"`), instead of living in normal flex flow
 * or reserving space via padding on the wrapper. Either of those alternatives
 * makes `<input>` NOT the visual bottom edge of this component's box, so any
 * external container that aligns a Button/select against this Input with
 * `alignItems: "flex-end"` (e.g. InviteMember.tsx, ServerModsPage.tsx's
 * search bar) ends up aligning against that reserved space instead of the
 * input itself, visually floating the button below the field. Anchoring
 * the error entirely outside the wrapper's box means the wrapper's bottom
 * edge is always the input's bottom edge, so flex-end alignment against
 * this component is always correct. Trade-off: an error appearing causes a
 * one-line layout shift in whatever sits below (rare, only on invalid
 * input), instead of every consumer permanently reserving space for an
 * error that's usually absent.
 */
export function Input({ label, error, id, ...rest }: InputProps) {
  const inputId = id ?? label.toLowerCase().replace(/\s+/g, "-")

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", position: "relative" }}>
      <label htmlFor={inputId} style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-medium)" }}>
        {label}
      </label>
      <input
        id={inputId}
        {...rest}
        aria-invalid={Boolean(error)}
        style={{
          padding: "10px 12px",
          fontSize: "var(--text-base)",
          // Fixed to match Button.tsx's own lineHeight exactly (both
          // "var(--line-height-ui)"). BUG FIXED (Joanfer's screenshots,
          // 2026-08-04, "hay botones desalineados a su texto"): without
          // this, <input> fell back to the browser's default line-height
          // ("normal", ~1.15-1.2 depending on font/browser) while Button
          // explicitly sets 1.1 -- same font-size, same vertical padding,
          // but a different line-height means a different total box
          // height, which is exactly the few-px vertical misalignment
          // visible wherever an Input and a Button sit side by side with
          // alignItems: "flex-end" (InviteMember.tsx, ServerModsPage.tsx's
          // search bar, CreateServerForm.tsx's RAM min/max row, and any
          // other Input+Button pairing in the app -- this fixes all of
          // them at once, the misalignment was never page-specific).
          lineHeight: "var(--line-height-ui)",
          borderRadius: "var(--radius-sm)",
          border: `1px solid ${error ? "var(--status-error)" : "var(--border-default)"}`,
          background: "var(--bg-surface)",
          color: "var(--text-primary)",
          transition: "border-color var(--duration-small) var(--ease-out)",
        }}
      />
      {error && (
        <span
          role="alert"
          style={{
            position: "absolute",
            left: 0,
            top: "100%",
            marginTop: "var(--space-1)",
            fontSize: "var(--text-xs)",
            color: "var(--status-error)",
            lineHeight: 1,
          }}
        >
          {error}
        </span>
      )}
    </div>
  )
}
